import { readSanctOfflineBonusPct } from '~/data/descenteSanctuaireShop'
import {
  OFFLINE_GOLD_POWER_MAX_PCT,
  resolveOfflineGoldPowerPct,
} from '../../../shared/offline_gold_model'

/** Aligné sur `GameController.OFFLINE_CAP_*` / boutique jetons. */
export const OFFLINE_CAP_BASE_HOURS = 24
export const OFFLINE_CAP_MAX_HOURS = 72
export const OFFLINE_CAP_STEP_HOURS = 6
export const OFFLINE_EFFICIENCY_BASE_LOGOUT_PERCENT = 100
/** @deprecated Alias */
export const OFFLINE_EFFICIENCY_BASE_ABSENT_PERCENT = OFFLINE_EFFICIENCY_BASE_LOGOUT_PERCENT
/** @deprecated Ancien max taux — migré vers puissance or */
export const OFFLINE_EFFICIENCY_MAX_BONUS_PERCENT = 12

export const OFFLINE_CHANNEL_BONUS_MAX_PERCENT = 15

export { OFFLINE_GOLD_POWER_MAX_PCT }

export function clampOfflineCapHours(value: unknown): number {
  const n = Math.floor(Number(value))
  if (!Number.isFinite(n)) return OFFLINE_CAP_BASE_HOURS
  return Math.max(OFFLINE_CAP_BASE_HOURS, Math.min(OFFLINE_CAP_MAX_HOURS, n))
}

export interface OfflineShopProgress {
  goldPowerPct?: number
  /** @deprecated */
  efficiencyBonusPct?: number
  /** @deprecated */
  goldCapBonusPct?: number
  killsFloorBonusPct?: number
  daycareCapBonusPct?: number
  tokenShopFullyUnlocked?: boolean
}

function clampInt(value: unknown, min: number, max: number): number {
  const n = Math.floor(Number(value))
  if (!Number.isFinite(n)) return min
  return Math.max(min, Math.min(max, n))
}

export function readOfflineGoldPowerPct(
  stats: Record<string, number> | undefined,
  progress?: OfflineShopProgress | null,
): number {
  const legacyEff = clampInt(
    (stats?._offlineEfficiencyBonusPct ?? progress?.efficiencyBonusPct ?? 0) as number,
    0,
    OFFLINE_EFFICIENCY_MAX_BONUS_PERCENT,
  )
  const legacyCap = clampInt(
    (stats?._offlineGoldCapBonusPct ?? progress?.goldCapBonusPct ?? 0) as number,
    0,
    OFFLINE_CHANNEL_BONUS_MAX_PERCENT,
  )
  const sanct = readSanctOfflineBonusPct(stats as Record<string, unknown> | undefined)
  const effWithSanct = Math.min(OFFLINE_EFFICIENCY_MAX_BONUS_PERCENT, legacyEff + sanct)
  const stored = stats?._offlineGoldPowerPct ?? progress?.goldPowerPct
  const goldPower =
    stored !== undefined && stored !== null ? Math.floor(Number(stored)) : undefined
  return resolveOfflineGoldPowerPct(goldPower, effWithSanct, legacyCap)
}

/** @deprecated Utiliser readOfflineGoldPowerPct */
export function readOfflineEfficiencyBonusPct(
  stats: Record<string, number> | undefined,
  _offlineGoldEfficiencyPercent?: number,
): number {
  return clampInt((stats?._offlineEfficiencyBonusPct ?? 0) as number, 0, OFFLINE_EFFICIENCY_MAX_BONUS_PERCENT)
}

export function readOfflineChannelBonusPct(
  stats: Record<string, number> | undefined,
  statKey: '_offlineGoldCapBonusPct' | '_offlineKillsFloorBonusPct' | '_offlineDaycareCapBonusPct',
  progressValue: number | undefined,
): number {
  const fromProgress = progressValue === undefined ? 0 : clampInt(progressValue, 0, OFFLINE_CHANNEL_BONUS_MAX_PERCENT)
  const fromStats = clampInt(stats?.[statKey] ?? 0, 0, OFFLINE_CHANNEL_BONUS_MAX_PERCENT)
  return Math.max(fromStats, fromProgress)
}

/** Fusionne la progression boutique renvoyée par `/game/load` dans `player.stats`. */
export function mergeOfflineShopProgressIntoStats(
  stats: Record<string, number>,
  progress: OfflineShopProgress | null | undefined,
): Record<string, number> {
  if (!progress || typeof progress !== 'object') return stats
  const next = { ...stats }
  if (typeof progress.goldPowerPct === 'number') {
    next._offlineGoldPowerPct = clampInt(progress.goldPowerPct, 0, OFFLINE_GOLD_POWER_MAX_PCT)
  }
  if (typeof progress.efficiencyBonusPct === 'number') {
    next._offlineEfficiencyBonusPct = clampInt(
      progress.efficiencyBonusPct,
      0,
      OFFLINE_EFFICIENCY_MAX_BONUS_PERCENT,
    )
  }
  if (typeof progress.goldCapBonusPct === 'number') {
    next._offlineGoldCapBonusPct = clampInt(progress.goldCapBonusPct, 0, OFFLINE_CHANNEL_BONUS_MAX_PERCENT)
  }
  if (typeof progress.killsFloorBonusPct === 'number') {
    next._offlineKillsFloorBonusPct = clampInt(progress.killsFloorBonusPct, 0, OFFLINE_CHANNEL_BONUS_MAX_PERCENT)
  }
  if (typeof progress.daycareCapBonusPct === 'number') {
    next._offlineDaycareCapBonusPct = clampInt(progress.daycareCapBonusPct, 0, OFFLINE_CHANNEL_BONUS_MAX_PERCENT)
  }
  next._offlineGoldPowerPct = readOfflineGoldPowerPct(next, progress)
  return next
}

export function shopPurchaseErrorMessage(
  err: unknown,
  t: (fr: string, en: string) => string,
): string {
  const msg =
    err && typeof err === 'object' && 'message' in err && typeof (err as { message: string }).message === 'string'
      ? (err as { message: string }).message
      : null
  if (msg && /not enough tokens/i.test(msg)) {
    return t(
      'Jetons insuffisants pour ce palier (recharge la page).',
      'Not enough tokens for this tier (reload the page).',
    )
  }
  if (msg && /maximum.*(efficiency|upgrade|reached|power)/i.test(msg)) {
    return t('Cette amélioration est déjà au maximum.', 'This upgrade is already maxed out.')
  }
  if (msg && /invalid.*(efficiency|upgrade|state|power)/i.test(msg)) {
    return t('État de progression incohérent — recharge la page.', 'Progress state mismatch — reload the page.')
  }
  return t("Erreur lors de l'achat.", 'Purchase error.')
}
